import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';
import { SetPasswordPayload } from '@/pages/auth/types';

interface VerifyTokenRequest {
  code: string;
}
interface AuthLoginResponse {
  access_token: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  name: string;
  fullname?: string;
  refresh_token: string;
  message: string;
  is_2fa_enabled: boolean;
  two_factor_required: boolean;
  temp_token?: string;
  otp_required: boolean;
}

interface GoogleAuthLinkResponse {
  google_signin_link: string;
}

interface LoginRequest {
  username: string;
  password: string;
}

interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  phone: string;
  country: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  fullname: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  is_2fa_enabled: boolean;
  two_factor_required: boolean;
  temp_token?: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
  }),
  endpoints: (builder) => ({
    fetchGoogleAuthLink: builder.query<string, void>({
      query: () => 'api/auth/google-redirect-link',
      transformResponse: (response: ApiResponse<GoogleAuthLinkResponse>) => {
        return response.data.google_signin_link;
      },
    }),
    verifyGoogleToken: builder.mutation<
      ApiResponse<AuthLoginResponse>,
      VerifyTokenRequest
    >({
      query: (body) => ({
        url: 'api/auth/google-verify-token',
        method: 'POST',
        body,
      }),
    }),
    userLogin: builder.mutation<ApiResponse<AuthLoginResponse>, LoginRequest>({
      query: (body) => ({
        url: 'api/auth/login',
        method: 'POST',
        body,
      }),
    }),
    userEmailLogin: builder.mutation<
      ApiResponse<AuthLoginResponse>,
      LoginRequest
    >({
      query: (body) => ({
        url: 'api/auth/login-with-email/',
        method: 'POST',
        body,
      }),
    }),
    userEmailLoginVerify: builder.mutation<
      ApiResponse<AuthLoginResponse>,
      { code: string; email: string; channel: string }
    >({
      query: (body) => ({
        url: 'api/auth/verify-login-email-otp/',
        method: 'POST',
        body,
      }),
    }),

    userRegister: builder.mutation<
      ApiResponse<{ message: string }>,
      { body: RegisterRequest; source?: string; referral_code?: string }
    >({
      query: ({ body, source, referral_code }) => {
        const queryParams = new URLSearchParams();
        if (source) {
          queryParams.append('source', source);
        }
        if (referral_code) {
          queryParams.append('referral_code', referral_code);
        }

        return {
          url: `/api/auth/register?${queryParams.toString()}`,
          method: 'POST',
          body,
        };
      },
    }),

    resendRegisterEmail: builder.mutation<
      ApiResponse<{ message: string }>,
      { email: string }
    >({
      query: (body) => ({
        url: '/api/auth/resend-verification-email/',
        method: 'POST',
        body,
      }),
    }),

    setPassword: builder.mutation<
      ApiResponse<{
        message: string;
        refresh_token: string;
        access_token: string;
        fullname: string;
        email: string;
      }>,
      SetPasswordPayload
    >({
      query: (body) => ({
        url: 'api/auth/set-password/',
        method: 'POST',
        body,
      }),
    }),
    forgetPassword: builder.mutation<
      ApiResponse<{ message: string }>,
      { email: string }
    >({
      query: (body) => ({
        url: 'api/auth/forgot-password/',
        method: 'POST',
        body,
      }),
    }),
    verifyRegisterEmail: builder.query<
      ApiResponse<{
        message: string;
        refresh_token: string;
        access_token: string;
        email: string;
        fullname: string;
      }>,
      { token: string }
    >({
      query: ({ token }) => `api/auth/verify-email/${token}/`,
    }),
    verify2FA: builder.mutation<
      LoginResponse,
      { code: string | number; temp_token: string }
    >({
      query: (body) => ({
        url: 'api/auth/verify-2fa/',
        method: 'POST',
        body,
      }),
    }),
    loginWithOtp: builder.mutation<ApiResponse<any>, { phone: string }>({
      query: (body) => ({
        url: 'api/auth/login-with-phone/',
        method: 'POST',
        body,
      }),
    }),
    verifyOtpLogin: builder.mutation<
      ApiResponse<any>,
      { code: string | number; phone: string }
    >({
      query: (body) => ({
        url: 'api/auth/login-with-phone-verify-otp/',
        method: 'POST',
        body,
      }),
    }),
    googleAuthCallback: builder.mutation<ApiResponse<any>, { code: string }>({
      query: (body) => ({
        url: 'api/oauth/google/callback/',
        method: 'POST',
        body,
      }),
    }),
    // resendEmailOtp: builder.mutation<
    //   ApiResponse<any>,
    //   { email: string; channel: string }
    // >({
    //   query: (body) => ({
    //     url: 'api/auth/resend-login-email-otp/',
    //     method: 'POST',
    //     body,
    //   }),
    // }),

    loginWithEmail: builder.mutation<
      ApiResponse<any>,
      { username: string; password: string }
    >({
      query: (body) => ({
        url: 'api/auth/login-with-email/',
        method: 'POST',
        body,
      }),
    }),
    verifyEmailOtp: builder.mutation<
      ApiResponse<any>,
      { email: string; otp: string }
    >({
      query: (body) => ({
        url: 'api/auth/verify-login-email-otp/',
        method: 'POST',
        body,
      }),
    }),
    resendEmailOtp: builder.mutation<ApiResponse<any>, { email: string }>({
      query: (body) => ({
        url: 'api/auth/resend-login-email-otp/',
        method: 'POST',
        body,
      }),
    }),
  }),
});
export const {
  useFetchGoogleAuthLinkQuery,
  useVerifyGoogleTokenMutation,
  useUserLoginMutation,
  useSetPasswordMutation,
  useForgetPasswordMutation,
  useUserRegisterMutation,
  useVerifyRegisterEmailQuery,
  useVerify2FAMutation,
  useResendRegisterEmailMutation,
  useLoginWithOtpMutation,
  useVerifyOtpLoginMutation,
  useGoogleAuthCallbackMutation,
  useUserEmailLoginMutation,
  useUserEmailLoginVerifyMutation,
  useResendEmailOtpMutation,
  useLoginWithEmailMutation,
  useVerifyEmailOtpMutation,
} = authApi;
